import Icon from "@expo/vector-icons/MaterialCommunityIcons";
import { Box, useAppTheme } from "@memorang/ui";
import { ModuleActionButton } from "components/ModuleActionButton";
import { ModuleMetadataChips } from "components/ModuleMetadataChips";
import { Image } from "expo-image";
import { Card, ProgressBar, Text } from "react-native-paper";
import type { DiagnosticCardProps } from "types/tag";

type Props = DiagnosticCardProps & {
	disabled?: boolean;
	isSessionConfig?: boolean;
};

const getSessionMetadata = (subtitle: string) => {
	const [typeLabel, durationLabel] = subtitle.split(/\s*[•·]\s*/);

	return {
		typeLabel,
		durationLabel,
	};
};

const RecommendationSessionCard = ({
	title,
	subtitle,
	handleClickCard,
	icon,
	numItems,
	numCompleted,
	loading,
	disabled,
	button,
	isSessionConfig,
	thumbnailSource,
}: Props) => {
	const isCompleted = numItems > 0 && numCompleted >= numItems;
	const progress = numItems > 0 ? Math.min(numCompleted / numItems, 1) : 0;
	const isModuleCard = Boolean(thumbnailSource);
	const { typeLabel, durationLabel } = getSessionMetadata(subtitle);
	const renderedSubtitle = isModuleCard ? typeLabel : subtitle;

	const theme = useAppTheme();
	return (
		<Card
			onPress={disabled ? undefined : handleClickCard}
			mode={isCompleted ? "elevated" : "outlined"}
			style={{
				overflow: "hidden",
				opacity: disabled ? 0.5 : 1,
				borderRadius: theme.roundness,
				backgroundColor: theme.colors.surface,
				height: isModuleCard ? 168 : undefined,
			}}
		>
			<Card.Content style={{ padding: 16, flex: isModuleCard ? 1 : undefined }}>
				<Box gap={isModuleCard ? 12 : 8}>
					<Box gap={12} flexDirection="row" alignItems="flex-start">
						{thumbnailSource ? (
							<Image
								source={thumbnailSource}
								style={{
									width: 44,
									height: 44,
									borderRadius: theme.roundness,
								}}
								contentFit="contain"
								alt={title}
							/>
						) : (
							<Icon name={icon} size={24} color={theme.colors.onSurface} />
						)}
						<Box flex={1} minWidth={0}>
							<Text
								variant="titleSmall"
								numberOfLines={isModuleCard ? 2 : 1}
								style={{
									marginRight: isModuleCard ? 0 : 24,
									maxWidth: isSessionConfig ? 200 : undefined,
									fontWeight: "700",
									lineHeight: 20,
								}}
							>
								{title}
							</Text>
							{isModuleCard ? null : (
								<Text
									variant="bodySmall"
									numberOfLines={1}
									style={{ color: theme.colors.onSurfaceVariant }}
								>
									{renderedSubtitle}
								</Text>
							)}
						</Box>
					</Box>
					<ProgressBar
						style={{
							height: 8,
							marginHorizontal: isModuleCard ? 4 : 0,
						}}
						progress={progress}
					/>
				</Box>
			</Card.Content>
			{button && (
				<Card.Actions
					style={{
						paddingHorizontal: 16,
						paddingTop: isModuleCard ? 0 : 8,
						paddingBottom: 16,
						justifyContent: isModuleCard ? "space-between" : "flex-end",
						alignItems: isModuleCard ? "flex-end" : "center",
					}}
				>
					{isModuleCard ? (
						<ModuleMetadataChips durationLabel={durationLabel} />
					) : null}
					<ModuleActionButton
						icon={button.icon}
						onPress={button.handleClick}
						disabled={disabled}
						loading={loading}
						mode={isModuleCard && button.cta === "Resume" ? "outlined" : "text"}
						cta={button.cta}
						style={
							isModuleCard && button.cta === "Resume"
								? { alignSelf: "flex-end" }
								: undefined
						}
					/>
				</Card.Actions>
			)}
		</Card>
	);
};

export default RecommendationSessionCard;
